import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map, catchError, tap, delay, first } from "rxjs/operators";
import { User } from "../models/auth.models";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { ChatService } from "src/app/pages/chat/services/chat.service";
import { CookieService } from "ngx-cookie-service";
import { TranslateService } from "@ngx-translate/core";
import { NotificationService } from "./notification.service";
import { PermissionsService } from "./permissions.service";
import { TopBarService } from "src/app/layouts/topbar/services/top-bar.services";
import { AgencyContextService } from "./agency-context.service";

@Injectable({ providedIn: "root" })
export class AuthService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  returnUrl: any;
  private isChatConneted = false;
  private isNotificationConneted = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private chatService: ChatService,
    public _cookiesService: CookieService,
    public translate: TranslateService,
    private notificationService: NotificationService,
    private permissionsService: PermissionsService,
    private topBarService: TopBarService,
    private agencyContextService: AgencyContextService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem("currentUser")!),
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.removeUserToLocalStorage();
    this.chatService.chatDisconnect().subscribe({
      next: () => {
        this.isChatConneted = false;
      },
    });
    this.translate.onLangChange.subscribe(() => {
      // console.log(this.translate.currentLang)
      this.getUser();
    });

    this.notificationService.chatDisconnect().subscribe({
      next: () => {
        this.isNotificationConneted = false;
      },
    });
  }

  /**
   * current user
   */
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /** "Ver como" ativo: o MASTER está a ver o sistema como outro utilizador. */
  public get isImpersonating(): boolean {
    return !!this.currentUserSubject.value?.impersonator;
  }

  /**
   * "Ver como" só de leitura. Nas entidades em que o servidor deixa escrever
   * (`impersonator.canWrite`, decidido no backend) o ecrã fica como para o
   * próprio utilizador.
   */
  public get isImpersonatingReadOnly(): boolean {
    const impersonator = this.currentUserSubject.value?.impersonator;
    return !!impersonator && impersonator.canWrite !== true;
  }

  /**
   * Performs the auth
   * @param email email of user
   * @param password password of user
   */
  login(email: string, password: string) {
    let dataLogin: any = { email, password };

    const usersDataPref = this.topBarService.getUserDataPref();
    if (usersDataPref?.length > 0) {
      const userDataPref = usersDataPref.filter(
        (ud) => ud.user.email === email,
      );
      if (userDataPref.length > 0) {
        const dataPref = [];
        for (const udp of userDataPref) {
          if (udp.agencies && udp.agencies.length > 0) {
            const dataCountry = {
              id: udp.id,
              agencies: udp.agencies?.map((ag) => ag.id),
              user: udp.user,
              entity: udp.entity,
            };
            dataPref.push(dataCountry);
          }
        }
        dataLogin = { email, password, dataPref };
      }
    }
    // const usersDataPref = this.topBarService.getUserDataPref()
    // if (usersDataPref?.length > 0) {
    //     const userDataPref = usersDataPref.find(ud => ud.user === this.currentUserValue.id)
    //     if (userDataPref && userDataPref.agencies && userDataPref.agencies.length > 0) {
    //         const dataCountry = {
    //             id: userDataPref.id, agencies: userDataPref.agencies?.map(ag => ag.id),
    //             user: this.currentUserValue.id, entity: this.currentUserValue.entity
    //         }
    //         dataLogin = { email, password, dataPref: { dataCountry } }
    //     }
    // }

    return this.http
      .post<any>(`${environment.apiUrl}/login`, dataLogin, {
        withCredentials: true,
      })
      .pipe(
        delay(100),
        tap((data) => {
          // console.log(data);
          if (data === 1) {
            localStorage.setItem("toast", "true");
            // O socket do chat NAO e ligado aqui de proposito: neste ponto ainda
            // nao conhecemos o papel do utilizador (o currentUser so passa a existir
            // depois de getUser() / saveUserToLocalStorage()). A ligacao e feita em
            // chatConnect(), que ja distingue AGENT de MASTER.
            // this.chatService.io.opts.withCredentials = true;
            // this.chatService.io.opts.reconnection= true;
          }
        }),
      );
  }

  sendVerificationCode(code: string) {
    return this.http
      .post(
        `${environment.apiUrl}/verification-code`,
        { code },
        { withCredentials: true },
      )
      .pipe(
        first(),
        tap((data) => {
          localStorage.setItem("toast", "true");
        }),
      );
  }

  resendVerificationCode() {
    return this.http
      .get(`${environment.apiUrl}/resend-verification-code`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  sendResetPasswordLink(email: string) {
    return this.http
      .post(
        `${environment.apiUrl}/send-reset-link`,
        { email },
        { withCredentials: true },
      )
      .pipe(
        first(),
        tap((data) => {
          localStorage.setItem("toast", "true");
        }),
      );
  }

  checkToken(token: any) {
    return this.http.get<any>(
      `${environment.apiUrl}/check-token?token=${token}`,
      { withCredentials: true },
    );
  }

  resetPassword(password: string) {
    return this.http.post<any>(
      `${environment.apiUrl}/change-password`,
      { password },
      { withCredentials: true },
    );
  }

  refreshCookie() {
    return this.http.get<any>(`${environment.apiUrl}/refresh`, {
      withCredentials: true,
    });
  }

  getUser() {
    const lang = this._cookiesService.get("lang") || "en";
    return this.http
      .get<User>(`${environment.apiUrl}/user-auth/${lang}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        tap((data) => {
          const permissions = data.usersPermissionsForEntities?.map(
            (p) => p.permissionForEntity?.name,
          );
          this.permissionsService.loadPermissions(permissions);
          this.permissionsService.userAuth = data;
        }),
      );
  }

  getLogo(): Observable<any> {
    if (localStorage.getItem(this.currentUserValue.id + "-logo")) {
      return of({
        logo: localStorage.getItem(this.currentUserValue.id + "-logo"),
        entityId: this.currentUserValue.entity,
      });
    } else {
      return this.http
        .get<{
          logo: string;
        }>(`${environment.apiUrl}/entities/entity-logo`, {
          withCredentials: true,
        })
        .pipe(
          tap((data) => {
            if (data?.logo)
              localStorage.setItem(
                this.currentUserValue.id + "-logo",
                data.logo,
              );
          }),
        );
    }
  }

  removeEntityLogo() {
    localStorage.removeItem(this.currentUserValue.id + "-logo");
  }

  saveUserToLocalStorage(user: User) {
    localStorage.setItem("currentUser", JSON.stringify(user));
    this.currentUserSubject.next(user);
    // Contexto global de agência: reposto aqui, antes de as páginas pedirem
    // dados. O seletor antigo de país/agências está escondido para o MASTER,
    // por isso as preferências antigas são apagadas — senão continuariam a
    // filtrar sem nada no ecrã que o mostrasse.
    if (AgencyContextService.isAvailableFor(user)) {
      this.topBarService.removeUserDataPrefToLocalStorage(user.id, user.entity);
    }
    this.agencyContextService.restore(user);
    // Em "Ver como" a sessão é a do MASTER: o chat e as notificações em tempo
    // real não a aceitariam para este utilizador.
    if (
      user?.role?.name &&
      !user?.impersonator &&
      this.permissionsService.isNotAdmin(user?.role?.name)
    ) {
      setTimeout(() => {
        this.chatConnect();
        this.notificationConnect();
      }, 2000);
    }
  }

  removeUserToLocalStorage() {
    localStorage.removeItem("currentUser");
    this.currentUserSubject.next(null!);
    this.dropRealtime();
  }

  /**
   * A sessão acabou. O socket partilhado (notificações + chat) tem a identidade
   * fixada no handshake, por isso acabar a sessão é desligá-lo. O `logout()` já o
   * fazia; o logout FORÇADO do jwt.interceptor (401 sem renovação possível) só
   * chamava `removeUserToLocalStorage()` e deixava o socket autenticado como o
   * utilizador anterior — e, com o chat dado como "ligado", o login seguinte no
   * mesmo separador (outra conta) nem voltava a ligar: ficava a receber e a
   * enviar com a identidade do anterior.
   *
   * As duas flags são repostas à mão porque o evento `disconnect` só dispara se o
   * socket estava ligado; se estava a meio de uma religação ficavam a `true` e o
   * login seguinte não ligava nem o chat nem as notificações.
   */
  private dropRealtime() {
    const active =
      this.isChatConneted ||
      this.isNotificationConneted ||
      this.notificationService.connected;
    this.isChatConneted = false;
    this.isNotificationConneted = false;
    if (!active) {
      // Arranque da aplicação (o construtor chama removeUserToLocalStorage()) ou
      // ADMIN, que nunca ligou: não há nada para desligar.
      return;
    }
    this.chatService.disconnect();
    this.notificationService.disconnectNotification();
  }

  /**
   * Logout the user
   */
  logout() {
    localStorage.removeItem(this.currentUserValue.id + "-logo");
    const storageKey = `lastNotificationShownDate__${this.currentUserValue.id}`;
    localStorage.removeItem(storageKey);
    if (
      this.currentUserValue?.role?.name &&
      this.permissionsService.isNotAdmin(this.currentUserValue?.role?.name)
    ) {
      this.chatService.disconnect();
      this.notificationService.disconnectNotification();
      // O evento `disconnect` so as repoe se o socket estava ligado.
      this.isChatConneted = false;
      this.isNotificationConneted = false;
    }
    return this.http
      .post<any>(`${environment.apiUrl}/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => {
          this.agencyContextService.clear();
          this.removeUserToLocalStorage();
        }),
      );
  }

  /**
   * Acesso ao chat no CRM = permissão VIEW_CHAT, pelo mesmo mecanismo que o
   * resto do CRM usa para permissões de entidade
   * (`PermissionsService.hasPermission`, que já faz o bypass de MASTER). Deixou
   * de depender do papel: MASTER e STORE OPERATOR também ligam, se tiverem
   * acesso. É só conveniência de UI — quem decide é o servidor
   * (`Entity.hasChat` + VIEW_CHAT, 403 sem acesso).
   */
  private hasChatAccess(currentUser: User): boolean {
    const roleName = currentUser?.role?.name;
    if (!roleName || !this.permissionsService.isNotAdmin(roleName)) {
      return false;
    }
    // `hasPermission` lê `permissionsService.userAuth`, que só é preenchido em
    // `getUser()`; quando o utilizador veio do localStorage ainda está vazio.
    if (!this.permissionsService.userAuth) {
      this.permissionsService.userAuth = currentUser;
    }
    return this.permissionsService.hasPermission("VIEW_CHAT");
  }

  chatConnect() {
    const currentUser = this.currentUserValue;
    if (!this.hasChatAccess(currentUser)) {
      // Só se desliga quem é utilizador de entidade; para os restantes (ADMIN,
      // sem sessão) nunca houve ligação e continua a não se fazer nada.
      if (this.permissionsService.isNotAdmin(currentUser?.role?.name)) {
        this.chatService.disconnect();
        this.isChatConneted = false;
      }
    } else if (!this.chatService.connected && !this.isChatConneted) {
      // O chat ja NAO tem socket proprio: `chatService.connect()` usa a ligacao do
      // NotificationService (real-time-notifications, docs/chat-socket-contract.md)
      // e nada aqui abre socket para o chat-v2. `chatService.connected` quer dizer
      // "chat ligado E autenticado", e `chatService.disconnect()` larga o chat sem
      // desligar o socket, que e das notificacoes.
      //
      // AVISO DE ORDEM DE DEPLOY: este build so pode ir para producao DEPOIS de o
      // real-time-notifications com o gateway de chat (src/chat) e o chat-v2 com
      // os endpoints HTTP de anexos estarem em producao. O front-end e estatico e
      // faz deploy muito mais depressa do que um contentor Nest; se subir sozinho,
      // o chat fica sem ninguem a responder do outro lado.
      this.chatService.connect();
      this.chatService.chatUsersOnline();
      this.isChatConneted = true;
    }
  }

  changePassword(data: any) {
    return this.http
      .post(`${environment.apiUrl}/auth/change-password`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  notificationConnect() {
    const currentUser = this.currentUserValue;
    if (!this.notificationService.connected && !this.isNotificationConneted) {
      // console.log("NotificationConnect");
      this.notificationService.connectedNotification();
      this.isNotificationConneted = true;
    }
  }

  backToSupportAccount() {
    this.chatService.disconnect();
    this.notificationService.disconnectNotification();
    this.isChatConneted = false;
    this.isNotificationConneted = false;
    return this.http
      .post(
        `${environment.apiUrl}/back-login-as`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Utilizadores da entidade que o MASTER pode ver como. */
  getImpersonableUsers() {
    return this.http
      .get<{ id: string; name: string; email: string; role?: { name?: string } }[]>(
        `${environment.apiUrl}/impersonation/users`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  startImpersonation(userId: string) {
    this.chatService.disconnect();
    this.notificationService.disconnectNotification();
    return this.http
      .post(
        `${environment.apiUrl}/impersonation/start`,
        { userId },
        { withCredentials: true },
      )
      .pipe(first());
  }

  stopImpersonation() {
    return this.http
      .post(
        `${environment.apiUrl}/impersonation/stop`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  getFirstRoute() {
    return this.http
      .get<any>(`${environment.apiUrl}/first-route`, { withCredentials: true })
      .pipe(first());
  }
}
